import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Card, Checkbox, Avatar, Tag, Typography, Space, Row, Col, message, Collapse, Empty, Button, Modal, Input, Menu, Dropdown } from "antd";
import { ApartmentOutlined, SaveOutlined, PlusOutlined, FileTextOutlined, EditOutlined, DeleteOutlined, MoreOutlined } from "@ant-design/icons";
import { AntdButton } from "../../../blueplanitUtils/InputField";
import { upsertHandbook, fetchAllHandbooks, deleteHandbook } from "../../../Slices/handBookSlice";
import { upsertHandbookAccess, fetchAllHandbookAccess } from "../../../Slices/handBookAccessSlice";
import { SERVER_URL } from "../../../databucket";

const { Title, Text } = Typography;
const { Panel } = Collapse;

// Handbooks are loaded from the `handbook` redux slice

// Optimized utility functions
const extractDepartments = (nestedDatabucket) => nestedDatabucket[0]?.children[1]?.children?.map(dept => ({ name: dept.name })) || [];
const filterActiveEmployees = (emps) => emps?.filter((e) => e.employeestatus === "Active" && e.department?.trim()) || [];

export default function HandbookManagement() {
  const dispatch = useDispatch();
  const { AllEmployees: allEmployees } = useSelector((s) => s.employee);
  const { NestedDatabucket } = useSelector((s) => s.dataBucket);
  const currentUser = useSelector((s) => s.user.user);
  const loginUser = useSelector((s) => s?.loginUserInfo?.loginUserDetails);

  const [selectedHandbook, setSelectedHandbook] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [handbookAccess, setHandbookAccess] = useState({});
  const [initialHandbookAccess, setInitialHandbookAccess] = useState({});
  const [isAddHandbookModalVisible, setIsAddHandbookModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingHandbook, setEditingHandbook] = useState(null);
  const { list: handbookList = [] } = useSelector((s) => s.handbooks || {});
  const { allHandbookAccess = [] } = useSelector((s) => s.handbookAccess || {});
  const handbooks = React.useMemo(() => handbookList.map(h => ({ ...h, id: h._id || h.id })), [handbookList]);
  const [newHandbookName, setNewHandbookName] = useState("");
  const [newHandbookDescription, setNewHandbookDescription] = useState("");
  const [handbookFile, setHandbookFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  // fetch handbooks from server
  useEffect(() => {
    dispatch(fetchAllHandbooks());
    dispatch(fetchAllHandbookAccess({ includeInactive: false }));
  }, [dispatch]);

  // Memoized data processing
  const departments = useMemo(() => extractDepartments(NestedDatabucket), [NestedDatabucket]);
  const employees = useMemo(() => filterActiveEmployees(allEmployees), [allEmployees]);

  const activeDepartments = useMemo(() =>
    departments.map((d) => ({
      ...d,
      employeeCount: employees.filter((e) => e.department === d.name).length
    })).filter(d => d.employeeCount > 0),
    [departments, employees]
  );

  const employeesByDepartment = useMemo(() => {
    const grouped = {};
    employees.forEach((emp) => {
      if (!grouped[emp.department]) grouped[emp.department] = [];
      grouped[emp.department].push(emp);
    });
    return grouped;
  }, [employees]);

  // Initialize handbook access state from fetched data
  useEffect(() => {
     if (employees.length === 0 || handbooks.length === 0 || isDirty) return;
      const accessMap = {};

      // Build a lookup: handbookId -> [employeeId,...]
      const handbookEmployeesMap = {};
      allHandbookAccess.forEach((rec) => {
        if (!rec) return;
        // Handbook-centric record: { handbookId, employees: [...] }
        if (rec.handbookId) {
          const hid = String(rec.handbookId);
          handbookEmployeesMap[hid] = (rec.employees || []).map(String);
        }
        // Employee-centric record: { employeeId, handbooks: [...] }
        else if (rec.employeeId) {
          const empId = String(rec.employeeId);
          (rec.handbooks || []).forEach((hbId) => {
            const hid = String(hbId);
            handbookEmployeesMap[hid] = handbookEmployeesMap[hid] || [];
            handbookEmployeesMap[hid].push(empId);
          });
        }
      });

      employees.forEach((emp) => {
        accessMap[emp.id] = {};
        handbooks.forEach((hb) => {
          const hid = String(hb.id);
          accessMap[emp.id][hb.id] = (handbookEmployeesMap[hid] || []).includes(String(emp.id));
        });
      });

      setHandbookAccess(accessMap);
      setInitialHandbookAccess(JSON.parse(JSON.stringify(accessMap)));
  }, [employees, handbooks, allHandbookAccess, isDirty]);

  // Optimized access utility functions
  const hasHandbookAccess = useCallback((empId, handbookId) =>
    (handbookAccess[empId]?.[handbookId]) || false,
    [handbookAccess]
  );

  const hasDepartmentHandbookAccess = useCallback((deptName, handbookId) => {
    const deptEmps = employeesByDepartment[deptName]?.map(e => e.id) || [];
    if (!deptEmps.length) return false;
    return deptEmps.every((empId) => hasHandbookAccess(empId, handbookId));
  }, [employeesByDepartment, hasHandbookAccess]);

  const hasDepartmentPartialHandbookAccess = useCallback((deptName, handbookId) => {
    const deptEmps = employeesByDepartment[deptName]?.map(e => e.id) || [];
    if (!deptEmps.length) return false;
    const hasAny = deptEmps.some((empId) => hasHandbookAccess(empId, handbookId));
    const hasAll = deptEmps.every((empId) => hasHandbookAccess(empId, handbookId));
    return hasAny && !hasAll;
  }, [employeesByDepartment, hasHandbookAccess]);

  // Toggle individual employee handbook access
  const toggleEmployeeHandbookAccess = useCallback((empId, handbookId) => {
      setIsDirty(true)
    setHandbookAccess((prev) => {
      const current = prev[empId] || {};
      return {
        ...prev,
        [empId]: {
          ...current,
          [handbookId]: !current[handbookId]
        }
      };
    });
  }, []);

  // Toggle entire department handbook access
  const toggleDepartmentHandbookAccess = useCallback((deptName, handbookId) => {
      setIsDirty(true)
    const deptEmps = employeesByDepartment[deptName]?.map(e => e.id) || [];
    setHandbookAccess((prev) => {
      const hasAll = deptEmps.every((empId) => hasHandbookAccess(empId, handbookId));
      const updated = { ...prev };

      deptEmps.forEach((empId) => {
        updated[empId] = {
          ...(updated[empId] || {}),
          [handbookId]: !hasAll
        };
      });

      return updated;
    });
  }, [employeesByDepartment, hasHandbookAccess]);

  // open add modal
  const openAddModal = () => {
    setIsEditing(false);
    setEditingHandbook(null);
    setNewHandbookName("");
    setNewHandbookDescription("");
    setHandbookFile(null);
    setIsAddHandbookModalVisible(true);
  };

  // open edit modal for a specific handbook
  const openEditModal = (handbook) => {
    setIsEditing(true);
    setEditingHandbook(handbook);
    setNewHandbookName(handbook.name || "");
    setNewHandbookDescription(handbook.description || "");
    setHandbookFile(null); // user may upload new file
    setIsAddHandbookModalVisible(true);
  };

  // delete a handbook
  const handleDeleteHandbook = useCallback(async (handbook) => {
    try {
      messageApi.loading({ content: 'Deleting handbook...', key: 'hb_delete' });
      const id = handbook.id || handbook._id;
      await dispatch(deleteHandbook({ id, changedBy: loginUser.id })).unwrap();
      messageApi.success({ content: 'Handbook deleted', key: 'hb_delete', duration: 2 });
      if (selectedHandbook?.id === id) {
        setSelectedHandbook(null);
      }
      setHandbookAccess((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((empId) => {
          const copy = { ...updated[empId] };
          delete copy[id];
          updated[empId] = copy;
        });
        return updated;
      });
    } catch (err) {
      console.error(err);
      messageApi.error({ content: err?.message || 'Failed to delete handbook', key: 'hb_delete' });
    }
  }, [dispatch, loginUser, messageApi, selectedHandbook]);

  // save (create or update) handbook
  const handleSaveHandbook = useCallback(async () => {
    if (!newHandbookName.trim()) {
      messageApi.error("Handbook name is required");
      return;
    }

    // If editing and nothing has changed (name, desc, or file), bail out
    const nameUnchanged = isEditing && newHandbookName === (editingHandbook?.name || '');
    const descUnchanged = isEditing && newHandbookDescription === (editingHandbook?.description || '');
    const fileUnchanged = !handbookFile; // no new file selected
    if (isEditing && nameUnchanged && descUnchanged && fileUnchanged) {
      messageApi.info('No changes detected');
      return;
    }

    try {
      setIsUploading(true);
      const actionKey = 'hb_upload';
      messageApi.loading({ content: isEditing ? 'Uploading handbook...' : 'Uploading handbook...', key: actionKey });

      let uploadedUrl = undefined;
      let filename = undefined;
      let mimetype = undefined;

      // only upload to S3 if a fresh file was chosen
      if (handbookFile) {
        const form = new FormData();
        form.append('files', handbookFile);

        const resp = await fetch(SERVER_URL + '/upload', {
          method: 'POST',
          body: form,
        });

        if (!resp.ok) throw new Error('File upload failed');

        const body = await resp.json();
        const first = body?.files?.[0];
        uploadedUrl = first?.url || first?.Location || first?.location;
        filename = handbookFile.name;
        mimetype = handbookFile.type;
      }

      messageApi.loading({ content: isEditing ? 'Saving changes...' : 'Saving handbook...', key: actionKey });

      const payload = await dispatch(upsertHandbook({
        id: isEditing ? (editingHandbook?.id || editingHandbook?._id) : undefined,
        name: newHandbookName,
        description: newHandbookDescription,
        ...(uploadedUrl ? { data: uploadedUrl, filename, mimetype } : {}),
        changedBy: loginUser.id,
      })).unwrap();

      if (!isEditing) {
        // Add new handbook id to access map for all employees
        const newId = payload._id || payload.id;
        if (newId) {
          setHandbookAccess((prev) => {
            const updated = { ...prev };
            Object.keys(updated).forEach((empId) => {
              updated[empId] = {
                ...(updated[empId] || {}),
                [newId]: false,
              };
            });
            return updated;
          });
        }
      }

      messageApi.success({ content: isEditing ? 'Handbook updated' : 'Handbook added', key: 'hb_upload', duration: 2 });
      setNewHandbookName("");
      setNewHandbookDescription("");
      setHandbookFile(null);
      setIsAddHandbookModalVisible(false);
      setIsEditing(false);
      setEditingHandbook(null);
      if (isEditing) {
        setSelectedHandbook(payload);
      }
    } catch (err) {
      console.error(err);
      messageApi.error({ content: err?.message || (isEditing ? 'Failed to update handbook' : 'Failed to add handbook'), key: 'hb_upload' });
    } finally {
      setIsUploading(false);
    }
  }, [newHandbookName, newHandbookDescription, handbookFile, messageApi, dispatch, loginUser, isEditing, editingHandbook]);

  // Save changes
  const handleSave = useCallback(async () => {
    try {
      const updates = [];

      // Build updates organized by handbook
      handbooks.forEach((handbook) => {
        const employeesWithAccess = [];
        const employeesWithoutAccess = [];

        employees.forEach((emp) => {
          const current = handbookAccess[emp.id]?.[handbook.id] || false;
          const initial = initialHandbookAccess[emp.id]?.[handbook.id] || false;

          if (current && !initial) {
            // Employee should have access but didn't before
            employeesWithAccess.push(emp.id);
          } else if (!current && initial) {
            // Employee should not have access but did before
            employeesWithoutAccess.push(emp.id);
          }
        });

        // Only create update if there are changes
        if (employeesWithAccess.length > 0 || employeesWithoutAccess.length > 0) {
          updates.push({
            handbookId: handbook.id,
            handbookName: handbook.name,
            employeesToAdd: employeesWithAccess,
            employeesToRemove: employeesWithoutAccess,
            changedBy: loginUser?.id || currentUser?.email || currentUser?.username || 'system',
            note: `Updated handbook "${handbook.name}" access`,
          });
        }
      });

      if (!updates.length) {
        messageApi.info("No changes to save");
        return;
      }

      messageApi.loading({ content: 'Saving handbook access settings...', key: 'saving' });

      // Dispatch upsertHandbookAccess for each handbook with changes
      const promises = updates.map((update) =>
        dispatch(upsertHandbookAccess(update)).unwrap()
      );

      await Promise.all(promises);

      messageApi.success({
        content: `Successfully updated access for ${updates.length} handbook(s)`,
        key: 'saving',
        duration: 3
      });
      setInitialHandbookAccess(JSON.parse(JSON.stringify(handbookAccess)));
      setIsDirty(false);
    } catch (err) {
      console.error(err);
      messageApi.error({
        content: err?.message || "Failed to save handbook access settings",
        key: 'saving',
        duration: 5
      });
    }
  }, [handbookAccess, initialHandbookAccess, employees, handbooks, messageApi, dispatch, loginUser, currentUser]);

  return (
    <div style={styles.container}>
      {contextHolder}

      <Header />

      <Row gutter={20}>
        <Col span={6}>
          <HandbookList
            handbooks={handbooks}
            selectedHandbook={selectedHandbook}
            onHandbookSelect={setSelectedHandbook}
            onAddHandbook={() => openAddModal()}
            onEditHandbook={openEditModal}
            onDeleteHandbook={handleDeleteHandbook}
          />
        </Col>

        <Col span={18}>
          <DepartmentAccessPanel
            selectedHandbook={selectedHandbook}
            activeDepartments={activeDepartments}
            employeesByDepartment={employeesByDepartment}
            hasHandbookAccess={hasHandbookAccess}
            hasDepartmentHandbookAccess={hasDepartmentHandbookAccess}
            hasDepartmentPartialHandbookAccess={hasDepartmentPartialHandbookAccess}
            onToggleEmployeeHandbookAccess={toggleEmployeeHandbookAccess}
            onToggleDepartmentHandbookAccess={toggleDepartmentHandbookAccess}
          />
        </Col>
      </Row>

      <SaveFooter onSave={handleSave} />

      <AddHandbookModal
        open={isAddHandbookModalVisible}
        onCancel={() => {
          setIsAddHandbookModalVisible(false);
          setIsEditing(false);
          setEditingHandbook(null);
        }}
        onAdd={handleSaveHandbook}
        handbookName={newHandbookName}
        onHandbookNameChange={setNewHandbookName}
        handbookDescription={newHandbookDescription}
        onHandbookDescriptionChange={setNewHandbookDescription}
        handbookFile={handbookFile}
        onHandbookFileChange={setHandbookFile}
        isUploading={isUploading}
        isEditing={isEditing}
      />
    </div>
  );
}

// Sub-components
const Header = React.memo(() => (
  <div style={styles.headerRow}>
    <div>
      <Title level={3} style={{ margin: 0, marginBottom: 4 }}>
        Handbook Access Management
      </Title>
      <Text type="secondary">Manage which employees can access handbooks</Text>
    </div>
  </div>
));

const HandbookList = React.memo(({ handbooks, selectedHandbook, onHandbookSelect, onAddHandbook, onEditHandbook, onDeleteHandbook }) => (
  <Card
    style={styles.listCard}
    bodyStyle={{ padding: 16 }}
    title={
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <FileTextOutlined />
        <span>Handbooks</span>
      </div>
    }
    extra={
      <Button
        type="primary"
        size="default"
        icon={<PlusOutlined />}
        onClick={onAddHandbook}
      >
        Add
      </Button>
    }
  >
    <div style={{ maxHeight: 600, overflowY: "auto" }}>
      {handbooks.length === 0 ? (
        <Empty description="No handbooks found" style={{ padding: "40px 0" }} />
      ) : (
        handbooks.map((handbook) => (
          <div
            key={handbook.id}
            style={styles.handbookItem(selectedHandbook?.id === handbook.id)}
            onClick={() => onHandbookSelect(handbook)}
          >
            <FileTextOutlined style={{ marginRight: 8 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text strong style={{ color: "inherit", display: "block" }}>
                {handbook.name}
              </Text>
              <Text style={{ fontSize: 12, color: selectedHandbook?.id === handbook.id ? "rgba(255,255,255,0.8)" : "#8c8c8c" }}>
                {handbook.description}
              </Text>
            </div>
            <Dropdown
              overlay={
                <Menu
                  onClick={({ key }) => {
                    if (key === 'edit') onEditHandbook(handbook);
                    if (key === 'delete') onDeleteHandbook(handbook);
                  }}
                >
                  <Menu.Item key="edit" icon={<EditOutlined />}>Edit</Menu.Item>
                  <Menu.Item key="delete" icon={<DeleteOutlined />}>Delete</Menu.Item>
                </Menu>
              }
              trigger={["click"]}
            >
              <MoreOutlined style={{ fontSize: 16, marginLeft: 8, cursor: 'pointer' }} onClick={(e) => e.stopPropagation()} />
            </Dropdown>
          </div>
        ))
      )}
    </div>
  </Card>
));

const DepartmentAccessPanel = React.memo(({
  selectedHandbook,
  activeDepartments,
  employeesByDepartment,
  hasHandbookAccess,
  hasDepartmentHandbookAccess,
  hasDepartmentPartialHandbookAccess,
  onToggleEmployeeHandbookAccess,
  onToggleDepartmentHandbookAccess,
}) => (
  <Card
    style={styles.detailCard}
    bodyStyle={{ padding: 0 }}
    title={
      selectedHandbook ? (
        <HandbookHeader handbook={selectedHandbook} />
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ApartmentOutlined />
          <span>Department Access</span>
        </div>
      )
    }
  >
    {selectedHandbook ? (
      <div style={{ maxHeight: 600, overflowY: "auto", padding: "16px" }}>
        <Collapse ghost defaultActiveKey={[]}>
          {activeDepartments.map((dept) => {
            const deptEmps = employeesByDepartment[dept.name] || [];
            if (!deptEmps.length) return null;

            return (
              <Panel
                header={
                  <DepartmentAccessHeader
                    department={dept}
                    employeeCount={deptEmps.length}
                    hasAccess={hasDepartmentHandbookAccess(dept.name, selectedHandbook.id)}
                    hasPartialAccess={hasDepartmentPartialHandbookAccess(dept.name, selectedHandbook.id)}
                    onToggle={(e) => {
                      e.stopPropagation();
                      onToggleDepartmentHandbookAccess(dept.name, selectedHandbook.id);
                    }}
                  />
                }
                key={dept.name}
              >
                <div style={{ paddingLeft: "24px" }}>
                  {deptEmps.map((emp) => (
                    <DepartmentEmployeeItem
                      key={emp.id}
                      employee={emp}
                      hasAccess={hasHandbookAccess(emp.id, selectedHandbook.id)}
                      onToggle={() => onToggleEmployeeHandbookAccess(emp.id, selectedHandbook.id)}
                    />
                  ))}
                </div>
              </Panel>
            );
          })}
        </Collapse>
      </div>
    ) : (
      <Empty
        description="Select a handbook to manage department access"
        style={{ padding: "60px 0" }}
      />
    )}
  </Card>
));

const HandbookHeader = React.memo(({ handbook }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
    <FileTextOutlined style={{ fontSize: 24, color: "#1677ff" }} />
    <div>
      <div style={{ fontWeight: 600, fontSize: 15 }}>
        {handbook.name}
      </div>
      <Text type="secondary" style={{ fontSize: 13 }}>
        {handbook.description}
      </Text>
    </div>
  </div>
));

const DepartmentAccessHeader = React.memo(({
  department,
  hasAccess,
  hasPartialAccess,
  onToggle,
  employeeCount
}) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <Checkbox
        checked={hasAccess}
        indeterminate={hasPartialAccess}
        onClick={onToggle}
      />
      <ApartmentOutlined />
      <Text strong>{department.name}</Text>
      <Text type="secondary" style={{ fontSize: 12 }}>
        ({employeeCount} employees)
      </Text>
      {hasPartialAccess && (
        <Tag color="orange" style={{ fontSize: 11 }}>
          Partial Access
        </Tag>
      )}
    </div>
  </div>
));

const DepartmentEmployeeItem = React.memo(({ employee, hasAccess, onToggle }) => (
  <div style={styles.departmentEmployeeItem}>
    <Checkbox checked={hasAccess} onChange={onToggle}>
      <Space size={8}>
        <Avatar
          size={24}
          src={employee.image}
          style={{ background: "#1677ff" }}
        >
          {employee.firstname?.charAt(0)}
        </Avatar>
        <div>
          <Text style={{ fontSize: 13 }}>
            {employee.firstname} {employee.lastname}
          </Text>
          <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
            {employee.designation || employee.role}
          </Text>
        </div>
      </Space>
    </Checkbox>
  </div>
));

const SaveFooter = React.memo(({ onSave }) => (
  <Card style={{ ...styles.mainCard, marginTop: 20 }}>
    <div style={styles.footer}>
      <Space>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#1677ff" }} />
        <Text type="secondary">Changes will be saved to handbook access permissions</Text>
      </Space>
      <AntdButton
        type="primary"
        size="large"
        icon={<SaveOutlined />}
        onClick={onSave}
        label="Save Handbook Access"
      />
    </div>
  </Card>
));

const AddHandbookModal = React.memo(({
  open,
  onCancel,
  onAdd,
  handbookName,
  onHandbookNameChange,
  handbookDescription,
  onHandbookDescriptionChange,
  handbookFile,
  onHandbookFileChange,
  isUploading,
  isEditing,
}) => (
  <Modal
    title={isEditing ? "Update Handbook" : "Add New Handbook"}
    open={open}
    onCancel={onCancel}
    onOk={onAdd}
    okText={isEditing ? "Update" : "Add"}
    cancelText="Cancel"
    confirmLoading={isUploading}
    okButtonProps={{ disabled: isUploading }}
  >
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <Text strong style={{ display: "block", marginBottom: 8 }}>Handbook Name</Text>
        <Input
          placeholder="Enter handbook name"
          value={handbookName}
          onChange={(e) => onHandbookNameChange(e.target.value)}
          disabled={isUploading}
        />
      </div>
      <div>
        <Text strong style={{ display: "block", marginBottom: 8 }}>Description</Text>
        <Input.TextArea
          placeholder="Enter handbook description"
          rows={4}
          value={handbookDescription}
          onChange={(e) => onHandbookDescriptionChange(e.target.value)}
          disabled={isUploading}
        />
      </div>

      <div>
        <Text strong style={{ display: "block", marginBottom: 8 }}>Upload File</Text>
        <input
          type="file"
          accept="*/*"
          onChange={(e) => onHandbookFileChange(e.target.files?.[0] || null)}
          disabled={isUploading}
          style={{ display: 'block' }}
        />
        {handbookFile && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Selected: {handbookFile.name}
          </Text>
        )}
      </div>

    </div>
  </Modal>
));

const styles = {
  container: {
    minHeight: "100vh",
    background: "#f5f5f5",
    padding: "24px"
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 24
  },
  mainCard: {
    borderRadius: 12,
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    border: "none"
  },
  listCard: {
    borderRadius: 12,
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    border: "none",
    height: "fit-content"
  },
  detailCard: {
    borderRadius: 12,
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    border: "none"
  },
  handbookItem: (isSelected) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    marginBottom: 8,
    borderRadius: 8,
    cursor: "pointer",
    background: isSelected ? "#1677ff" : "#fff",
    color: isSelected ? "#fff" : "#333",
    border: "none",
    transition: "all 0.2s"
  }),
  departmentEmployeeItem: {
    padding: "8px 0",
    borderBottom: "1px solid #f0f0f0"
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
};